/**
 * Early Access cart + shipment grouping helpers (Shopify Cart AJAX API).
 *
 * Liquid: snippets/product-ea-storefront-data.liquid, snippets/cart-ea-context.liquid
 * Wired: blocks/buy-buttons.liquid, snippets/product-card.liquid,
 *        sections/main-cart.liquid, snippets/cart-drawer.liquid,
 *        assets/quick-add.js, assets/size-quick-add-modal.js
 *
 * Note: /cart.js line items do not include product metafields. Snapshot _ea_* line
 * properties at add-to-cart (PDP form + quick-add). Optional merge with a
 * handle→productInfo cache using mergeLineWithProductMeta().
 */

/** @type {readonly string[]} */
export const EA_SNAPSHOT_PROPERTY_KEYS = [
  '_ea_shipping_group_key',
  '_ea_expected_ship_date',
  '_ea_fulfillment_short',
  '_ea_fulfillment_long',
  '_ea_fulfillment_group_name',
];

/** Shown to customers on checkout (underscore keys stay internal). */
export const EA_CHECKOUT_VISIBLE_LINE_KEYS = ['Est. ship'];

/**
 * Shopify Admin/API sometimes persists "-" for empty text metafields; treat as missing for snapshots + grouping.
 * @param {unknown} v
 * @returns {string}
 */
export function normalizeEaSnapshotString(v) {
  if (v == null) return '';
  const s = String(v).trim();
  if (s === '' || s === '-') return '';
  return s;
}

/**
 * @param {Record<string, unknown>} props
 * @returns {Record<string, string>}
 */
function normalizeEaPropertiesObject(props) {
  const out = { ...props };
  for (const key of [...EA_SNAPSHOT_PROPERTY_KEYS, ...EA_CHECKOUT_VISIBLE_LINE_KEYS]) {
    if (!Object.prototype.hasOwnProperty.call(out, key)) continue;
    const n = normalizeEaSnapshotString(out[key]);
    if (n === '') delete out[key];
    else out[key] = n;
  }
  const hasEaSnapshot = EA_SNAPSHOT_PROPERTY_KEYS.some((k) => normalizeEaSnapshotString(out[k]) !== '');
  if (hasEaSnapshot) {
    const drop = normalizeEaSnapshotString(out.Drop);
    const est = normalizeEaSnapshotString(out['Est. ship']);
    let ship = est;
    if (drop !== '' && est !== '') ship = `${drop} · ${est}`;
    else if (drop !== '') ship = drop;
    delete out.Drop;
    if (ship !== '') out['Est. ship'] = ship;
    else delete out['Est. ship'];
  }
  return out;
}

/**
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {Record<string, string>}
 */
export function compactEaLineProperties(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of EA_SNAPSHOT_PROPERTY_KEYS) {
    const s = normalizeEaSnapshotString(raw[key]);
    if (s !== '') out[key] = s;
  }
  const drop = normalizeEaSnapshotString(raw.Drop);
  const est = normalizeEaSnapshotString(raw['Est. ship']);
  let ship = est;
  if (drop !== '' && est !== '') ship = `${drop} · ${est}`;
  else if (drop !== '') ship = drop;
  if (ship !== '') out['Est. ship'] = ship;
  return out;
}

/**
 * @param {string | undefined} url
 * @returns {string}
 */
export function parseProductHandleFromLineUrl(url) {
  if (!url) return '';
  const m = String(url).match(/\/products\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

/**
 * @param {object} item - /cart.js item
 * @returns {object}
 */
export function normalizeCartLineItem(item) {
  const rawProps = item && typeof item.properties === 'object' && item.properties !== null ? { ...item.properties } : {};
  const props = normalizeEaPropertiesObject(rawProps);
  const handle = parseProductHandleFromLineUrl(item?.url);
  return {
    key: item?.key,
    id: item?.id,
    variant_id: item?.variant_id,
    product_id: item?.product_id,
    quantity: item?.quantity,
    final_line_price: item?.final_line_price,
    line_price: item?.line_price,
    final_price: item?.final_price,
    title: item?.title,
    url: item?.url,
    product_handle: handle,
    properties: props,
    /** EA snapshot fields duplicated for grouping */
    _ea_shipping_group_key: props._ea_shipping_group_key,
    _ea_expected_ship_date: props._ea_expected_ship_date,
    _ea_fulfillment_short: props._ea_fulfillment_short,
    _ea_fulfillment_long: props._ea_fulfillment_long,
    _ea_fulfillment_group_name: props._ea_fulfillment_group_name,
  };
}

/**
 * @param {object} line - normalizeCartLineItem output
 * @param {Record<string, object>} [productMetaByHandle] - optional handle → productInfo.ea from storefront JSON
 * @returns {object}
 */
export function mergeLineWithProductMeta(line, productMetaByHandle) {
  const handle = line.product_handle || '';
  const meta = handle && productMetaByHandle && productMetaByHandle[handle] ? productMetaByHandle[handle] : null;
  const fallbackGroup = normalizeEaSnapshotString(meta?.early_access_fulfillment_shipping_group) || null;
  const merged = { ...line };
  merged.shipment_group_key =
    merged._ea_shipping_group_key || merged.properties?._ea_shipping_group_key || fallbackGroup || '';
  merged.early_access_fulfillment_shipping_group = fallbackGroup || '';
  return merged;
}

/**
 * @param {object} cart - raw /cart.js JSON
 * @returns {{ token: string, item_count: number, total_price: number, currency: string, items: object[], attributes: object }}
 */
export function normalizeCartResponse(cart) {
  const c = cart && typeof cart === 'object' ? cart : {};
  return {
    token: c.token || '',
    item_count: Number(c.item_count) || 0,
    total_price: Number(c.total_price) || 0,
    currency: c.currency || '',
    items: Array.isArray(c.items) ? c.items.map((item) => normalizeCartLineItem(item)) : [],
    attributes: c.attributes && typeof c.attributes === 'object' ? { ...c.attributes } : {},
  };
}

/**
 * Fetches /cart.js and returns normalized cart info for drawers / analytics.
 * @returns {Promise<{ token: string, item_count: number, total_price: number, currency: string, items: object[], attributes: object }>}
 */
export async function getCartInfo() {
  const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
  const url = `${root.endsWith('/') ? root.slice(0, -1) : root}/cart.js`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
  const cart = await res.json();
  return normalizeCartResponse(cart);
}

/**
 * Groups cart lines by shipment key for UI (single shipping charge at checkout; grouped messaging).
 * @param {Array<object>} lines - use mergeLineWithProductMeta() output for best fallback behavior
 * @returns {Array<{ groupKey: string, heading: string, lines: object[] }>}
 */
export function groupCartLinesByShipment(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const order = [];
  /** @type {Map<string, { groupKey: string, heading: string, lines: object[] }>} */
  const map = new Map();

  for (const line of list) {
    const rawKey =
      line.shipment_group_key ||
      line._ea_shipping_group_key ||
      line.properties?._ea_shipping_group_key ||
      line.early_access_fulfillment_shipping_group ||
      '';
    const groupKey = rawKey && String(rawKey).trim() !== '' ? String(rawKey).trim() : '__ship_now__';

    if (!map.has(groupKey)) {
      const date = line._ea_expected_ship_date || line.properties?._ea_expected_ship_date || '';
      const short = line._ea_fulfillment_short || line.properties?._ea_fulfillment_short || '';
      const gname = line._ea_fulfillment_group_name || line.properties?._ea_fulfillment_group_name || '';
      let heading = 'Ships now';
      if (groupKey === '__ship_now__') {
        heading = 'Ships now';
      } else if (date) {
        heading = `Ships from ${date}`;
      } else if (gname) {
        heading = `Shipment: ${gname}`;
      } else if (short) {
        heading = short;
      } else {
        heading = 'Shipment group';
      }
      map.set(groupKey, { groupKey, heading, lines: [] });
      order.push(groupKey);
    }
    map.get(groupKey).lines.push(line);
  }

  return order.map((k) => map.get(k));
}

/**
 * @param {ParentNode | null} [root]
 * @returns {Record<string, object>} handle → parsed product EA object
 */
export function collectProductEaMetaFromDom(root = document) {
  /** @type {Record<string, object>} */
  const out = {};
  if (!root || typeof root.querySelectorAll !== 'function') return out;
  root.querySelectorAll('script.product-ea-storefront-json[data-product-handle]').forEach((el) => {
    const handle = el.getAttribute('data-product-handle');
    if (!handle) return;
    try {
      const data = JSON.parse(el.textContent || '{}');
      if (data && data.ea) out[handle] = data.ea;
    } catch {
      // ignore
    }
  });
  return out;
}

/**
 * Reads EA snapshot JSON from a `script.product-card__ea-atc` inside container (product card, form, etc.).
 * @param {ParentNode | null | undefined} container
 * @returns {Record<string, string>}
 */
export function readEaLinePropertiesFromEmbeddedScript(container) {
  const script = container?.querySelector?.('script.product-card__ea-atc');
  if (!script?.textContent) return {};
  try {
    const raw = JSON.parse(script.textContent.trim());
    return compactEaLineProperties(raw);
  } catch {
    return {};
  }
}

/**
 * Reads presale checkout line properties from a `script.product-card__presale-atc` inside container.
 * @param {ParentNode | null | undefined} container
 * @returns {Record<string, string>}
 */
export function readPresaleLinePropertiesFromEmbeddedScript(container) {
  const script = container?.querySelector?.('script.product-card__presale-atc');
  if (!script?.textContent) return {};
  try {
    const raw = JSON.parse(script.textContent.trim());
    if (!raw || typeof raw !== 'object') return {};
    /** @type {Record<string, string>} */
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
      const normalized = normalizeEaSnapshotString(value);
      if (normalized !== '') out[key] = normalized;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * @param {ParentNode | null} el
 * @returns {Record<string, string>}
 */
export function readEaLinePropertiesFromCard(el) {
  const card = el instanceof Element ? el.closest('product-card') : null;
  return readEaLinePropertiesFromEmbeddedScript(card);
}

/**
 * @param {ParentNode | null} el
 * @returns {Record<string, string>}
 */
export function readPresaleLinePropertiesFromCard(el) {
  const card = el instanceof Element ? el.closest('product-card') : null;
  return readPresaleLinePropertiesFromEmbeddedScript(card);
}

if (typeof window !== 'undefined') {
  window.SaboEaCart = {
    EA_SNAPSHOT_PROPERTY_KEYS,
    EA_CHECKOUT_VISIBLE_LINE_KEYS,
    normalizeEaSnapshotString,
    compactEaLineProperties,
    parseProductHandleFromLineUrl,
    normalizeCartLineItem,
    mergeLineWithProductMeta,
    normalizeCartResponse,
    getCartInfo,
    groupCartLinesByShipment,
    collectProductEaMetaFromDom,
    readEaLinePropertiesFromCard,
    readEaLinePropertiesFromEmbeddedScript,
    readPresaleLinePropertiesFromCard,
    readPresaleLinePropertiesFromEmbeddedScript,
  };
}
